import * as d3 from "d3";
import _ from "lodash";
import $ from 'jquery';
import { useCallback, useContext, useEffect, useRef, useState } from "react";
import { Button, Divider, Form, FormProps, Header, Icon, Input, InputOnChangeData, InputProps, Label, Message, Modal, Segment, Table, TextArea } from "semantic-ui-react";
import { restRequest } from "../../../http";
import { PlotChannelColors } from "../../../models/common_model";
import { MeasurementPlotInfo, MeasurementPlot, VectorData, LimitData } from "../../../models/testing_model";
import { downloadCSV, fieldError, generateParametersKey, untilStable } from "../../../utils";
import { ModalControlData, useLocalStorage, useModalControl } from "../../../hooks/commonHooks";
import { notificationControl } from "../../../controls/notification-control";
import { TestingVisualSearchParameterContext } from "../../../context";

const statusColors = ['', PlotChannelColors[0], 'darkred'];
const statisticsColors = ['green', 'purple'];
export function VectorPlot(props: { testing_id: string, measurementKey: string, info?: MeasurementPlotInfo, requestParams?: { status?: number, serial_number?: string, start_time?: string, end_time?: string, testing_station_id?: string, employee_id?: string }, onLoading: (status: boolean) => void }) {
    const { testing_id, measurementKey, info, requestParams } = props;
    const [searchParameters, setParentSearchParameter] = useContext(TestingVisualSearchParameterContext)!;
    
    const sVectorData = useState<MeasurementPlot[]>([]);
    const sFilteredVectorData = useState<MeasurementPlot[]>([]);
    const sSelectedMouseXY = useState<number[]>([0, 0]);
    const sMeasurementReadyKey = useState('');
    const sSelectedVectorData = useState<MeasurementPlot[]>([]);
    const sForSelectVectorData = useState<MeasurementPlot[]>([]);
    const sForSelectSelectedVectorData = useState<MeasurementPlot[]>([]);
    const sSearchedVectorData = useState<MeasurementPlot[]>([]);
    const sSerialNumberKeyword = useState<string>(_.get(searchParameters, 't_4_3_serial_keyword', ''));
    const sChannelIndex = useState(parseInt(_.get(searchParameters, 't_4_3_channel', '1') || '1'));
    const sStatus = useState((_.get(searchParameters, 't_4_3_status', '1_1') || '1_1').split('_').map(v => v === '1'));
    const sAverage = useState(_.get(searchParameters, 't_4_3_average', '0') === '1');
    const sMedian = useState(_.get(searchParameters, 't_4_3_median', '0') === '1');
    const sStatisticsData = useState<{color: string, values: number[][]}[]>([]);
    const sFullSize = useState<number[]>();
    const svgRootRef = useRef<HTMLDivElement>(null);
    const canvasContextRef = useRef<CanvasRenderingContext2D>();
    const operationContextRef = useRef<CanvasRenderingContext2D>();
    const xRef = useRef<d3.ScaleLogarithmic<number, number, never> | d3.ScaleLinear<number, number, never>>();
    const yRef = useRef<d3.ScaleLinear<number, number, never>>();
    const lineRef = useRef<d3.Line<[number, number]>>();
    const sIsPreparing = useState(true);
    const TOOLBAR_WIDTH = 250;
    const bisectDate = d3.bisector<number[], number>(function (d) { return d[0] }).left;
    const margin = ({ top: 20, right: 30, bottom: 30, left: 50 });

    const [vectorLimitFilter, setVectorLimitFilter] = useLocalStorage<VectorFilter>(`${testing_id}_${measurementKey}_${sChannelIndex[0]}_vector_limit`, {x: [], lower: [], upper: []});
    const vectorFilterEditModalControl = useModalControl<VectorFilter>();

    useEffect(() => {
        setParentSearchParameter((ps: any) => {
            const data = {tab_path: 't_4_3'} as any;
            data.t_4_3_serial_keyword = sSerialNumberKeyword[0] || '';
            data.t_4_3_channel = sChannelIndex[0];
            data.t_4_3_status = sStatus[0].map(v => v ? '1' : '0').join('_');
            data.t_4_3_average = sAverage[0] ? '1' : '0';
            data.t_4_3_median = sMedian[0] ? '1' : '0';

            return {...ps, ...data};
        });
    }, [sSerialNumberKeyword[0], sChannelIndex[0], sStatus[0], sAverage[0], sMedian[0]]);

    useEffect(() => {
        let filteredData = sVectorData[0].filter(v => {
            if (sStatus[0][v.status - 1]) {
                return true;
            }

            return false;
        });
        if (vectorLimitFilter.x.length > 0) {
            filteredData = filteredData.map(vectorData => {
                let lineValues: number[][] = vectorData.values!;
                const lineScale = d3.scaleLinear().domain(lineValues.map(v => v[0])).range(lineValues.map(v => v[1]));
                for (let i = 0; i <= vectorLimitFilter.x.length; i++) {
                    const x = vectorLimitFilter.x[i];
                    const y = lineScale(x);
                    if (vectorLimitFilter.lower[i] > y || y > vectorLimitFilter.upper[i]) {
                        return undefined as any;
                    }
                }

                const xs = d3.sort(_.uniq(lineValues.map(v => v[0]).concat(vectorLimitFilter.x)));

                return {...vectorData, values: xs.map(x => [x, lineScale(x)])};
            }).filter(v => !!v);
        }
        sFilteredVectorData[1](filteredData);

        const statisticsData = []
        if (sAverage[0] || sMedian[0]) {
            const availableFiles = filteredData.filter(d => d.values && d.values.length > 0);
            if (availableFiles.length > 0) {
                const firstOne = (availableFiles.find(d => d.status == 1) || availableFiles[0]);

                const defaultXValues = (firstOne.values as number[][]).map(r => r[0]);

                const yScales: d3.ScaleLinear<number, number, never>[] = [];
                availableFiles.map(f => {
                    const result: number[][] = f.values!;
                    yScales.push(d3.scaleLinear().domain(result.map(d => d[0])).range(result.map(d => d[1])));
                });

                const averageValues = [];
                const medianValues = [];


                for(const x of defaultXValues) {
                    const ys: number[] = [];
                    for (const yScale of yScales) {
                        ys.push(yScale(x));
                    }

                    if(sAverage[0]) {
                        averageValues.push([x, d3.mean(ys)!]);
                    }

                    if (sMedian[0]) {
                        medianValues.push([x, d3.median(ys)!]);
                    }
                }


                if (sAverage[0]) {
                    statisticsData.push({color: statisticsColors[0], values: averageValues});
                }

                if (sMedian[0]) {
                    statisticsData.push({color: statisticsColors[1], values: medianValues});
                }
            }
        }
        sStatisticsData[1](statisticsData);
    }, [sVectorData[0], sStatus[0], vectorLimitFilter, sAverage[0], sMedian[0]]);

    useEffect(() => {
        const kw = sSerialNumberKeyword[0];
        if (!kw || kw.length < 3) {
            sSearchedVectorData[1]([]);
            return;
        }

        const filteredData: [number, MeasurementPlot][] = []
        for (const d of sVectorData[0]) {
            const i = d.serial_number.indexOf(kw);
            if (i !== -1) {
                filteredData.push([i, d]);
            }
        }

        sSearchedVectorData[1](filteredData.sort((a, b) => a[0] > b[0] ? 1 : (a[0] == b[0] ? 0 : -1)).map(v => v[1]).slice(0, 10));
    }, [sVectorData[0], sSerialNumberKeyword[0]]);


    useEffect(() => {
        props.onLoading(true);
        const params = { ...requestParams, measurement_key: measurementKey, channel: sChannelIndex[0] };
        restRequest<MeasurementPlot[]>(`testings/overview_plot_measurement/${testing_id}`, params, 'post', undefined, `overview_plot_measurement/${testing_id}/${generateParametersKey(params)}`).then(vs => {
            sVectorData[1](vs.map(v => {
                let result: VectorData;
                try {
                    result = JSON.parse(v.result);
                } catch(e) {
                    console.error('can parse vector value: ', v.result, v.file_name)
                    return null as any;
                }

                if (result.x_values && result.y_values && _.isArray(result.y_values)) {
                    const values: number[][] = [];
                    result.y_values.forEach((y, i) => {
                        if (_.isNumber(y)) {
                            values.push([result.x_values[i], y]);
                        }
                    })
                    const vv = { ...v, values, result: '' }

                    return vv;
                }
                return null as any;
            }).filter(v => v));

            sMeasurementReadyKey[1](measurementKey);
        }).finally(() => {
            props.onLoading(false);
        });
    }, [testing_id, measurementKey, requestParams, sChannelIndex[0]]);


    useEffect(() => {
        sIsPreparing[1](true);
        return untilStable<number>(() => svgRootRef.current!.offsetWidth, (svgWidth) => {
            if (sMeasurementReadyKey[0] != measurementKey) {
                return;
            }

            const fullWidth = svgWidth - TOOLBAR_WIDTH;
            const totalHeight = window.innerHeight - $(svgRootRef.current!).offset()!.top;
            const fullHeight = Math.min(fullWidth * 0.618, totalHeight);

            let canvas = d3.select(svgRootRef.current!).select<HTMLCanvasElement>('canvas.base');
            let operationCanvas = d3.select(svgRootRef.current!).select<HTMLCanvasElement>('canvas.operation');

            if (canvas.size() === 0) {
                canvas = d3.select(svgRootRef.current!)
                    .append("canvas")
                    .attr('class', 'base');

                operationCanvas = d3.select(svgRootRef.current!)
                    .append("canvas")
                    .attr('class', 'operation')
                    .style('background', 'rgba(255,255,255,0)')
                    .style('position', 'absolute')
                    .style('z-index', '1')
                    .style('top', '0')
                    .style('left', '0');
            }

            canvas.attr("width", fullWidth).attr("height", fullHeight);
            operationCanvas.attr("width", fullWidth).attr("height", fullHeight);
            sFullSize[1]([fullWidth, fullHeight]);
            canvasContextRef.current = canvas.node()!.getContext('2d')!;
            operationContextRef.current = operationCanvas.node()!.getContext('2d')!;


            const miniY = yRef.current && yRef.current.domain() ? Math.abs(yRef.current.invert(2) - yRef.current.invert(0)) : 2;

            let currentSelectedVectorData: MeasurementPlot[] = [];
            sForSelectVectorData[1]([]);
            sIsPreparing[1](false);
            operationCanvas.on('mouseover', function () {
                //todo hide cursor lines
            })
                .on('mouseout', function () {
                    sSelectedMouseXY[1]([0, 0]);
                    sSelectedVectorData[1]([]);
                })
                .on('mousemove', function (event) {
                    if (sFilteredVectorData[0] && xRef.current && yRef.current) {
                        const mouse = d3.pointer(event);
                        const mouseX = xRef.current.invert(mouse[0] - margin.left);
                        const mouseY = yRef.current.invert(mouse[1] - margin.top);

                        const selectedUnits: [number, MeasurementPlot][] = [];
                        sFilteredVectorData[0].forEach((d, index) => {
                            let i = bisectDate(d.values!, mouseX);
                            if (i < d.values!.length) {
                                if (i > 0 && mouseX - d.values![i - 1][0] < d.values![i][0] - mouseX) {
                                    i -= 1;
                                }

                                const yDiff = Math.abs(d.values![i][1] - mouseY);
                                if (yDiff < miniY) {
                                    selectedUnits.push([yDiff, d])
                                }
                            }
                        });

                        const selectedVectorData = selectedUnits.sort((a, b) => a[0] > b[0] ? 1 : (a[0] === b[0] ? 0 : -1)).slice(0, 10).map(s => s[1]);
                        sSelectedMouseXY[1]([mouseX, mouseY]);
                        sSelectedVectorData[1](selectedVectorData);

                        currentSelectedVectorData = selectedVectorData;
                    }
                }).on('click', function () {
                    sForSelectVectorData[1](currentSelectedVectorData);
                    sForSelectSelectedVectorData[1]([]);
                });
        }, 200);
    }, [sFilteredVectorData[0], measurementKey, sMeasurementReadyKey[0]]);

    useEffect(() => {
        if (sIsPreparing[0]) {
            return;
        }
        const operationContext = operationContextRef.current;
        if (operationContext && sFullSize[0]) {
            operationContext.clearRect(0, 0, sFullSize[0][0], sFullSize[0][1]);
            if (lineRef.current) {
                const line = lineRef.current.context(operationContext);
                operationContext.save();
                operationContext.translate(margin.left, margin.top);
                for (const d of _.uniq(sSelectedVectorData[0].concat(sForSelectSelectedVectorData[0]))) {
                    operationContext.beginPath();
                    line(d.values as any);
                    operationContext.lineWidth = 1;
                    operationContext.strokeStyle = 'yellow';
                    operationContext.stroke();
                }

                if (vectorLimitFilter.x.length > 0) {
                    operationContext.beginPath();
                    line(vectorLimitFilter.x.map((x, i) => [x, vectorLimitFilter.lower[i]]));
                    line(vectorLimitFilter.x.map((x, i) => [x, vectorLimitFilter.upper[i]]));
                    operationContext.lineWidth = 2;
                    operationContext.strokeStyle = "#00FF00";
                    operationContext.stroke();
                }

                for (const v of sStatisticsData[0]) {
                    operationContext.beginPath();
                    line(v.values as any);
                    operationContext.lineWidth = 1;
                    operationContext.strokeStyle = v.color;
                    operationContext.stroke();
                }

                operationContext.restore();
            }
        }
    }, [sSelectedVectorData[0], sForSelectSelectedVectorData[0], sIsPreparing[0], vectorLimitFilter, sStatisticsData[1]]);

    useEffect(() => {
        if (sIsPreparing[0] || !sFilteredVectorData[0] || !info) {
            return;
        }

        const [fullWidth, fullHeight] = sFullSize[0]!;
        const context = canvasContextRef.current!;
        context.clearRect(0, 0, fullWidth, fullHeight);

        const width = fullWidth - margin.left - margin.right;
        const height = fullHeight - margin.top - margin.bottom;

        const limits = info.limits[sChannelIndex[0] - 1] as LimitData<VectorData>;

        const filterExtent = d3.extent(vectorLimitFilter.lower.concat(vectorLimitFilter.upper));
        const xDomain = d3.extent(_.flatten(sFilteredVectorData[0].map(d => d3.extent(d.values!.map(v => v[0])))).concat(d3.extent(limits.lower?.x_values || [])).concat(d3.extent(limits.upper?.x_values || [])) as number[]) as number[];
        const yDomain = d3.extent(_.flatten(sFilteredVectorData[0].map(d => d3.extent(d.values!.map(v => v[1])))).concat(d3.extent(limits.lower?.y_values || [])).concat(d3.extent(limits.upper?.y_values || [])).concat(filterExtent) as number[]) as number[];
        const x = (xDomain[1] > 1000 ? d3.scaleLog() : d3.scaleLinear()).domain(xDomain).range([margin.left, width - margin.right]);
        xRef.current = x;
        const xAxis = () => {
            const tickCount = 10,
                tickSize = 6,
                ticks = x.ticks(tickCount),
                tickFormat = x.tickFormat(tickCount, 'd');

            context.beginPath();
            ticks.forEach(function (d) {
                context.moveTo(x(d), height);
                context.lineTo(x(d), 0);
            });
            context.strokeStyle = "gray";
            context.stroke();

            const xAxisTitle = xDomain[xDomain.length - 1] >= 1500 ? 'Frequency(Hz)' : '';
            context.beginPath();
            context.strokeStyle = "black";
            const metrics = context.measureText(xAxisTitle);
            const textWidth = metrics.width;
            context.fillText(xAxisTitle, x(xDomain[0]) + x(xDomain[1]) / 2 - x(xDomain[0]) / 2 - textWidth / 2, fullHeight - 30);

            context.beginPath();
            context.lineTo(0, height - tickSize);
            context.lineTo(width, height - tickSize);
            context.strokeStyle = "gray";
            context.stroke();

            context.textAlign = "center";
            context.textBaseline = "top";
            ticks.forEach(function (d) {
                context.fillText(tickFormat(d), x(d), height);
            });
        }

        const y = d3.scaleLinear().domain(yDomain).range([height - margin.bottom, margin.top]);
        yRef.current = y;

        const yAxis = () => {
            const tickCount = 10,
                tickSize = 6,
                tickPadding = 3,
                ticks = y.ticks(tickCount),
                tickFormat = y.tickFormat(tickCount);

            context.beginPath();
            ticks.forEach(function (d) {
                context.moveTo(0, y(d));
                context.lineTo(width, y(d));
            });
            context.strokeStyle = "black";
            context.stroke();

            context.beginPath();
            context.moveTo(-tickSize, 0);
            context.lineTo(0.5, 0);
            context.lineTo(0.5, height);
            context.lineTo(-tickSize, height);
            context.strokeStyle = "black";
            context.stroke();

            context.textAlign = "right";
            context.textBaseline = "middle";
            ticks.forEach(function (d) {
                context.fillText(tickFormat(d), -tickSize - tickPadding, y(d));
            });

            const unit = info!.result_unit || info!.limit_unit;
            const yAxisTitle = info!.measurement_name + (unit ? ` (${unit})` : '');
            context.save();
            context.rotate(-Math.PI / 2);
            context.textAlign = "right";
            context.textBaseline = "top";
            context.strokeStyle = "black";
            const metrics = context.measureText(yAxisTitle);
            const textWidth = metrics.width;
            context.fillText(yAxisTitle, -(height / 2) + textWidth / 2, -margin.left);
            context.restore();
        }

        const baseLine = d3.line()
            .x(function (d: number[], i: number) { return x(d[0]) })
            .y(function (d: number[], i: number) { return y(d[1]) });

        lineRef.current = baseLine;

        const line = baseLine.context(context);

        context.save();
        context.translate(margin.left, margin.top);
        xAxis();
        yAxis();

        sFilteredVectorData[0].forEach((vectorData, index) => {
            const lineValues: number[][] = vectorData.values!;

            context.beginPath();
            line(lineValues as any);
            context.lineWidth = 1;
            context.strokeStyle = statusColors[vectorData.status];
            context.stroke();
        });

        if (limits.lower) {
            context.beginPath();
            line(limits.lower.x_values.map((v, i) => [v, limits.lower?.y_values[i]] as any));
            context.lineWidth = 3;
            context.strokeStyle = "#FF0000";
            context.stroke();
        }

        if (limits.upper) {
            context.beginPath();
            line(limits.upper.x_values.map((v, i) => [v, limits.upper?.y_values[i]] as any));
            context.lineWidth = 3;
            context.strokeStyle = "#FF0000";
            context.stroke();
        }

        context.restore();

        return () => {
            context.clearRect(0, 0, fullWidth, fullHeight);
        }

    }, [info, sFilteredVectorData[0], sIsPreparing[0], vectorLimitFilter]);

    return <>
        <div>
            {info && <div style={{ width: `${TOOLBAR_WIDTH}px`, paddingRight: '1rem', paddingTop: '50px', float: 'left' }}>
                <Divider></Divider>
                <Button.Group compact>
                    {info?.channels.map(c => <Button key={c} secondary basic={sChannelIndex[0] !== c} onClick={e => {
                        sChannelIndex[1](c);
                    }}>CH{c}</Button>)}
                </Button.Group>
                <Divider></Divider>
                <Button.Group compact>
                    {['Pass', 'Fail'].map((s, i) => <Button key={i} style={{ background: sStatus[0][i] ? statusColors[i + 1] : null, color: '#fff' }} color="grey" onClick={e => {
                        sStatus[1](v => v.map((v, ii) => i === ii ? !v : v));
                    }}>{s}</Button>)}
                </Button.Group>
                <Divider></Divider>
                <Button.Group compact>
                    <Button style={{ background: sAverage[0] ? statisticsColors[0] : null, color: '#fff' }} color="grey" onClick={e => {
                        sAverage[1](v => !v);
                    }}>Average</Button>
                    <Button style={{ background: sMedian[0] ? statisticsColors[1] : null, color: '#fff' }} color="grey" onClick={e => {
                        sMedian[1](v => !v);
                    }}>Median</Button>
                </Button.Group>

                <Header attached="top" size="tiny">
                    Filtered By Limit
                    <a onClick={e => {
                        e.preventDefault();
                        vectorFilterEditModalControl.data = vectorLimitFilter;
                        vectorFilterEditModalControl.setOpen(true);
                    }} href="#" style={{ float: 'right' }}>
                        <Icon name="edit" color="blue" />
                    </a>
                </Header>
                <Segment attached="bottom" size="tiny">
                    <Table celled compact size="small">
                        <Table.Header>
                            <Table.Row>
                                <Table.HeaderCell>X</Table.HeaderCell>
                                <Table.HeaderCell>Lower</Table.HeaderCell>
                                <Table.HeaderCell>Upper</Table.HeaderCell>
                            </Table.Row>
                        </Table.Header>
                        <Table.Body>
                            {vectorLimitFilter.x.map((x, i) => <Table.Row key={i}>
                                <Table.Cell>{x}</Table.Cell>
                                <Table.Cell>{vectorLimitFilter.lower[i]}</Table.Cell>
                                <Table.Cell>{vectorLimitFilter.upper[i]}</Table.Cell>
                            </Table.Row>)}
                        </Table.Body>
                    </Table>
                </Segment>

                <Header attached="top" size="tiny">Selected</Header>
                <Segment attached="bottom" size="tiny">
                    <div>
                        X: {sSelectedMouseXY[0][0]}<br />
                        Y: {sSelectedMouseXY[0][1]}
                    </div>

                    <Divider></Divider>
                    <div className="button_container">
                        {sSelectedVectorData[0].map(d => <Label key={d.file_name} size="tiny" basic style={{ color: statusColors[d.status] }}>{d.serial_number}</Label>)}
                    </div>

                    <Divider></Divider>
                    <div className="button_container">
                        {sForSelectVectorData[0].map(d => <Button style={{ opacity: sForSelectSelectedVectorData[0].includes(d) ? 1 : 0.314, background: statusColors[d.status], color: '#fff' }} onClick={e => {
                            e.preventDefault();
                            if (sForSelectSelectedVectorData[0].includes(d)) {
                                sForSelectSelectedVectorData[1](vs => vs.filter(v => v !== d));
                            } else {
                                sForSelectSelectedVectorData[1](vs => [...vs, d]);
                            }
                        }} onDoubleClick={(e: any) => {
                            e.preventDefault();
                            window.open(`/testing_file_visual/${d.file_id}/3/${measurementKey}`, '_blank');
                        }} key={d.file_name} size="mini" compact title={d.file_name + ' (Double Click to Detail)'}>{d.serial_number}</Button>)}
                    </div>
                </Segment>

                <Divider></Divider>

                <Header attached="top">
                    <Input icon placeholder='Search By Serial...' size="small">
                        <input value={sSerialNumberKeyword[0]} onChange={e => sSerialNumberKeyword[1](e.target.value)} />
                        <Icon name='search' />
                    </Input>
                </Header>
                <Segment attached="bottom" size="tiny">
                    <div className="button_container">
                        {sSearchedVectorData[0].map(d => <Button key={d.file_name} onClick={e => {
                            e.preventDefault();

                            let contains = false;
                            if (sForSelectSelectedVectorData[0].includes(d)) {
                                sForSelectSelectedVectorData[1](vs => vs.filter(v => v !== d));
                                contains = true;
                            }

                            if (sSelectedVectorData[0].includes(d)) {
                                sSelectedVectorData[1](vs => vs.filter(v => v !== d));
                                contains = true;
                            }

                            if (!contains) {
                                sForSelectSelectedVectorData[1](vs => [...vs, d]);
                            }

                        }} onDoubleClick={(e: any) => {
                            e.preventDefault();
                            window.open(`/testing_file_visual/${d.file_id}/3/${measurementKey}`, '_blank');
                        }} style={{ opacity: (sForSelectSelectedVectorData[0].includes(d) || sSelectedVectorData[0].includes(d)) ? 1 : 0.314, background: statusColors[d.status], color: '#fff' }} title={d.file_name + ' (Double Click to Detail)'} size="mini" compact>{d.serial_number}</Button>)}
                    </div>
                </Segment>

                <Divider></Divider>

                <div>
                    <Button.Group size="small">
                        <Button primary onClick={e => {
                            e.preventDefault();
                            const availableFiles = sFilteredVectorData[0].filter(d => d.values && d.values.length > 0);
                            if (availableFiles.length === 0) {
                                notificationControl.showError('No filtered unit!');
                                return;
                            }

                            const firstOne = (availableFiles.find(d => d.status == 1) || availableFiles[0]);

                            const defaultXValues = (firstOne.values as number[][]).map(r => r[0]);

                            const csvData: string[][] = [];

                            csvData.push(['X Values']);
                            const yScales: d3.ScaleLinear<number, number, never>[] = [];
                            availableFiles.map(f => {
                                csvData[0].push(f.file_name);
                                const result: number[][] = f.values!;
                                yScales.push(d3.scaleLinear().domain(result.map(d => d[0])).range(result.map(d => d[1])));
                            });

                            for(const x of defaultXValues) {
                                const row = [x.toFixed(6).replace(/0+$/, '')];

                                for (const yScale of yScales) {
                                    row.push(yScale(x).toFixed(6).replace(/0+$/, ''))
                                }
                                csvData.push(row);
                            }

                            downloadCSV(csvData.map(v => v.join(',')).join('\n'), `${sMeasurementReadyKey[0]}.csv`)
                        }}>CSV</Button>
                    </Button.Group>
                    &nbsp;&nbsp;
                    <Button.Group size="small">
                        <Button primary onClick={e => {
                            e.preventDefault();
                            const availableFiles = sFilteredVectorData[0].filter(d => d.values && d.values.length > 0);
                            if (availableFiles.length === 0) {
                                notificationControl.showError('No filtered unit!');
                                return;
                            }

                            const csvData: string[][] = [];

                            csvData.push([]);
                            availableFiles.map(f => {
                                csvData[0].push(f.file_name + ' X');
                                csvData[0].push('Y');
                            });

                            let xIndex = 0;
                            while(true) {
                                let finded = false;
                                const rowValues = _.flatten(availableFiles.map(f => {
                                    const results = f.values as number[][];
                                    if (results.length > xIndex) {
                                        finded = true;
                                        const result = results[xIndex];
                                        return [!_.isNil(result[0]) ? result[0].toFixed(6).replace(/0+$/, '') : '', !_.isNil(result[1]) ? result[1].toFixed(6).replace(/0+$/, '') : '']
                                    } else {
                                        return ['', '']
                                    }
                                }));
                                
                                if (finded) {
                                    csvData.push(rowValues);
                                } else {
                                    break;
                                }

                                ++xIndex;
                            }

                            downloadCSV(csvData.map(v => v.join(',')).join('\n'), `${sMeasurementReadyKey[0]}.csv`)
                        }}>CSV (Raw)</Button>
                    </Button.Group>
                </div>
            </div>}
            <div style={{ float: 'right', position: 'relative', width: `calc(100% - ${TOOLBAR_WIDTH}px)` }} ref={svgRootRef}>
            </div>
        </div>

        <VectorLimitEditor modalControl={vectorFilterEditModalControl} onFinish={data => {
            setVectorLimitFilter(data);

            vectorFilterEditModalControl.setOpen(false);
        }}></VectorLimitEditor>
    </>;
}


type VectorFilter = { x: number[], lower: number[], upper: number[] }

function VectorLimitEditor(props: { modalControl: ModalControlData<VectorFilter | undefined>, onFinish: (data: VectorFilter) => void }) {
    const { modalControl, onFinish } = props;
    const [formData, setFormData] = useState<{text: string}>({} as any);
    const [invalidError, setInvalidError] = useState<any>({});

    useEffect(() => {
        const space = ' '.repeat(60);
        let text = `x${space}lower${space}upper`;
        modalControl.data?.x.forEach((x, i) => {
            try {
                text += `\n${x.toFixed(6).replace(/\.?0+$/, '')}${space}${modalControl.data?.lower[i].toFixed(6).replace(/\.?0+$/, '')}${space}${modalControl.data?.upper[i].toFixed(6).replace(/\.?0+$/, '')}`;
            } catch (e) {}
        });


        setFormData({text});
    }, [modalControl.data]);

    const onSubmit = useCallback((event: React.FormEvent<HTMLFormElement>, formProps: FormProps) => {
        event.preventDefault();
        const error: any = {};

        const text = formData.text;
        const data: VectorFilter = { x: [], lower: [], upper: [] };
        try {
            const lines = text.split('\n');
            lines.splice(1).forEach(line => {
                const values = line.split(/\s+/);
                if (values.length >= 3) {
                    const x = parseFloat(values[0].trim());
                    const lower = parseFloat(values[1].trim());
                    const upper = parseFloat(values[2].trim());

                    if (isNaN(x) || isNaN(lower) || isNaN(upper)) {
                        error.text = 'Invalid format';
                        return false;
                    }

                    data.x.push(x);
                    data.lower.push(lower);
                    data.upper.push(upper);
                }
           });
        } catch (e) {
            error.text = 'Invalid format';
        }

        if (!_.isEmpty(error)) {
            setInvalidError(error);
        } else {
            onFinish(data);
        }
    }, [formData, onFinish])

    return <>
        <Modal {...modalControl.props} size="large">
            <Modal.Header>Edit Limit</Modal.Header>
            <Modal.Content>
                <Form onSubmit={onSubmit} error>
                    <Message info>Split the values of x, lower, upper by arbitrarily white spaces in every row.</Message>
                    <Form.Input error={fieldError('text', invalidError)}>
                        <TextArea name="text" value={formData.text || ''} onChange={(e, d) => setFormData(fd => ({...fd, text: d.value as string}))} rows={10}></TextArea>
                    </Form.Input>
                    <div>
                        <Button primary>Submit</Button>
                        &nbsp;&nbsp;&nbsp;&nbsp;&nbsp;
                        <Button as="a" onClick={e => modalControl.setOpen(false)}>Cancel</Button>
                    </div>
                </Form>
            </Modal.Content>
        </Modal >
    </>
}